// src/pages/Demo.js

import React, { useState } from 'react';
import { Box, Typography, Button } from '@mui/material';

import loKtrLogo from '../assets/loKtrLogo.png';
import TopBar from '../components/TopBar/TopBar';
import Background from '../components/Background/Background';
import ContactUsDialog from '../components/ContactUsDialog/ContactUsDialog';

import packageJson from '../../package.json';

const Demo = () => {
  // === LAYOUT POSITIONING CONTROLS ===
  const gridTopPosition = '70px';
  const bodyFontSize = 'clamp(13px, min(1.5vw, 2.5vh), 28px)';

  const [dialogOpen, setDialogOpen] = useState(false);

  const handleOpenDialog = () => setDialogOpen(true);
  const handleCloseDialog = () => setDialogOpen(false);

  return (
    <Background>
      <style>
        {`
          @keyframes pulse-ring {
            0% { box-shadow: 0 0 0 0 rgba(51, 51, 51, 0.5); }
            70% { box-shadow: 0 0 0 25px rgba(51, 51, 51, 0); }
            100% { box-shadow: 0 0 0 0 rgba(51, 51, 51, 0); }
          }
          @keyframes shimmer-sweep {
            0% { left: -100%; }
            20% { left: 200%; }
            100% { left: 200%; }
          }
        `}
      </style>

      {/* === INJECT TOP MENU BAR === */}
      <TopBar activeButton='demo' />

      {/* === VERTICAL FLEX LAYOUT === */}
      <Box
        sx={{
          position: 'absolute',
          top: gridTopPosition,
          bottom: '20px',
          left: '20px',
          right: '20px',
          display: 'flex',
          flexDirection: 'column',
          gap: 0,
          zIndex: 1,
        }}>
        {/* ROW 1: Logo Image */}
        <Box
          sx={{
            flex: 0.6,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            p: 2,
            minHeight: 0,
            overflow: 'hidden',
          }}>
          <img
            src={loKtrLogo}
            alt='loKtr Logo'
            style={{
              maxWidth: '100%',
              maxHeight: '90%',
              objectFit: 'contain',
              marginTop: '50px',
            }}
          />
        </Box>

        {/* ROW 2: 4-Column Grid for Content */}
        <Box
          sx={{
            flex: 2.4,
            display: 'grid',
            gridTemplateColumns: '0.6fr 2.4fr 2.4fr 0.6fr',
            gap: '24px',
            minHeight: 0,
          }}>
          {/* Column 1: Blank */}
          <Box />

          {/* Column 2: Demo Section */}
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'flex-start',
              p: { xs: 1.5, md: 3 },
              pt: { xs: 4, md: 8 },
              color: '#000000',
              minHeight: 0,
            }}>
            <Typography
              sx={{
                fontFamily: '"Inter", sans-serif',
                fontSize: bodyFontSize,
                fontWeight: 700,
                lineHeight: 1.2,
                mb: 0.5,
              }}>
              Experience the Demo
            </Typography>
            <Typography
              sx={{
                fontFamily: '"Inter", sans-serif',
                fontSize: bodyFontSize,
                fontWeight: 500,
                lineHeight: 1.2,
                mb: 0.5,
              }}>
              Step into your clients' shoes and see{' '}
              <span style={{ color: 'royalblue', fontWeight: 500 }}>loKtr</span> in
              action.
            </Typography>
            <Typography
              sx={{
                fontFamily: '"Inter", sans-serif',
                fontSize: bodyFontSize,
                fontWeight: 500,
                lineHeight: 1.2,
                mb: 0.5,
                display: 'flex',
                alignItems: 'flex-start',
              }}>
              <span
                style={{
                  display: 'inline-block',
                  fontSize: '1.2em',
                  marginRight: '12px',
                  marginTop: '-4px',
                }}>
                •
              </span>
              <span>
                The Client Perspective: Explore the exact interactive interface your
                clients will use to view your projects and locations.
              </span>
            </Typography>
            <Typography
              sx={{
                fontFamily: '"Inter", sans-serif',
                fontSize: bodyFontSize,
                fontWeight: 500,
                lineHeight: 1.2,
                mb: 0.5,
                display: 'flex',
                alignItems: 'flex-start',
              }}>
              <span
                style={{
                  display: 'inline-block',
                  fontSize: '1.2em',
                  marginRight: '12px',
                  marginTop: '-4px',
                }}>
                •
              </span>
              <span>
                Focused Preview: This demonstration is securely restricted to the Home
                page, giving you a clear, focused look at the core mapping features.
              </span>
            </Typography>

            {/* Launch Demo Button */}
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                mt: 'auto',
                pt: 4,
              }}>
              <Button
                variant='contained'
                onClick={() => window.open('https://demo.loktr.net/login', '_blank')}
                sx={{
                  width: '240px',
                  height: '60px',
                  backgroundColor: '#333333',
                  color: '#FFFFFF',
                  border: '2px solid #FFFFFF',
                  fontWeight: 'normal',
                  fontSize: '1.1rem',
                  borderRadius: '30px',
                  opacity: 1,
                  overflow: 'hidden',
                  animation: 'pulse-ring 2s infinite cubic-bezier(0.66, 0, 0, 1)',
                  '&:hover': { backgroundColor: '#555555', borderColor: '#FFFFFF' },
                  '&::after': {
                    content: '""',
                    position: 'absolute',
                    top: 0,
                    left: '-100%',
                    width: '50%',
                    height: '100%',
                    background:
                      'linear-gradient(to right, rgba(255,255,255,0) 0%, rgba(255,255,255,0.3) 50%, rgba(255,255,255,0) 100%)',
                    transform: 'skewX(-25deg)',
                    animation: 'shimmer-sweep 3s infinite',
                  },
                }}>
                LAUNCH DEMO
              </Button>
            </Box>
          </Box>

          {/* Column 3: Contact Section */}
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'flex-start',
              p: { xs: 1.5, md: 3 },
              pt: { xs: 4, md: 8 },
              color: '#000000',
              minHeight: 0,
            }}>
            <Typography
              sx={{
                fontFamily: '"Inter", sans-serif',
                fontSize: bodyFontSize,
                fontWeight: 700,
                lineHeight: 1.2,
                mb: 0.5,
              }}>
              Get Started with{' '}
              <span style={{ color: 'royalblue', fontWeight: 700 }}>loKtr</span>{' '}
            </Typography>
            <Typography
              sx={{
                fontFamily: '"Inter", sans-serif',
                fontSize: bodyFontSize,
                fontWeight: 500,
                lineHeight: 1.2,
                mb: 0.5,
              }}>
              Ready to see your own projects on the map?
            </Typography>
            <Typography
              sx={{
                fontFamily: '"Inter", sans-serif',
                fontSize: bodyFontSize,
                fontWeight: 500,
                lineHeight: 1.2,
                mb: 0.5,
                display: 'flex',
                alignItems: 'flex-start',
              }}>
              <span
                style={{
                  display: 'inline-block',
                  fontSize: '1.2em',
                  marginRight: '12px',
                  marginTop: '-4px',
                }}>
                •
              </span>
              <span>
                Free Trial: Contact us today to arrange a comprehensive, no-obligation
                trial.
              </span>
            </Typography>
            <Typography
              sx={{
                fontFamily: '"Inter", sans-serif',
                fontSize: bodyFontSize,
                fontWeight: 500,
                lineHeight: 1.2,
                mb: 0.5,
                display: 'flex',
                alignItems: 'flex-start',
              }}>
              <span
                style={{
                  display: 'inline-block',
                  fontSize: '1.2em',
                  marginRight: '12px',
                  marginTop: '-4px',
                }}>
                •
              </span>
              <span>
                Your Data, Live: We will populate the app with your actual company data so
                you can experience the exact value{' '}
                <span style={{ color: 'royalblue', fontWeight: 500 }}>loKtr</span> brings
                to your business.
              </span>
            </Typography>

            {/* Contact Us Button */}
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                mt: 'auto',
                pt: 4,
              }}>
              <Button
                variant='contained'
                onClick={handleOpenDialog} // Triggers the contact dialog
                sx={{
                  width: '240px',
                  height: '60px',
                  backgroundColor: '#333333',
                  color: '#FFFFFF',
                  border: '2px solid #FFFFFF',
                  fontWeight: 'normal',
                  fontSize: '1.1rem',
                  borderRadius: '30px',
                  opacity: 1,
                  '&:hover': { backgroundColor: '#555555', borderColor: '#FFFFFF' },
                }}>
                CONTACT US
              </Button>
            </Box>
          </Box>

          {/* Column 4: Blank */}
          <Box />
        </Box>

        {/* ROW 3: Invisible Spacer (Preserves layout math without rendering text) */}
        <Box sx={{ flex: 0.75, minHeight: 0 }} />
      </Box>

      {/* ========================================================================= */}
      {/* === UNIVERSAL FOOTER === */}
      {/* ========================================================================= */}
      <Box
        sx={{
          position: 'absolute',
          bottom: '20px', // Anchors to the bottom of the screen
          left: 0,
          right: 0,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '8px',
          backgroundColor: 'transparent',
          zIndex: 10,
        }}>
        <Typography
          onClick={handleOpenDialog} // Triggers the contact dialog
          sx={{
            fontFamily: '"Inter", sans-serif',
            fontSize: 'clamp(14px, 1.5vw, 18px)',
            fontWeight: 400,
            color: '#000000',
            opacity: 0.9,
            cursor: 'pointer',
            transition: 'opacity 0.2s ease',
            '&:hover': { textDecoration: 'underline', opacity: 1 },
          }}>
          info@loktr.net
        </Typography>
        <Box sx={{ textAlign: 'center' }}>
          <Typography
            sx={{
              fontFamily: '"Inter", sans-serif',
              fontSize: 'clamp(11px, 1vw, 14px)',
              color: '#000000',
              lineHeight: 1.3,
              opacity: 0.8,
            }}>
            Version: {packageJson.version}
          </Typography>
          <Typography
            sx={{
              fontFamily: '"Inter", sans-serif',
              fontSize: 'clamp(11px, 1vw, 14px)',
              color: '#000000',
              lineHeight: 1.3,
              opacity: 0.8,
            }}>
            (c) RTA Technology Pty Ltd. All rights reserved.
          </Typography>
        </Box>
      </Box>

      {/* === EXTRACTED CONTACT FORM POPUP === */}
      <ContactUsDialog open={dialogOpen} onClose={handleCloseDialog} />
    </Background>
  );
};

export default Demo;
